import * as React from "react";
import { format, subMonths } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DateRange as DayPickerDateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";

export type DateRange = [Date, Date];

type DateRangePickerCommonProps = {
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  dateFormat?: string;
};

type DateRangePickerValueProps = DateRangePickerCommonProps & {
  /** Current range as a `Date` tuple. */
  value?: DateRange | null;
  onChange?: (range: DateRange | null) => void;
  after?: never;
  before?: never;
};

type DateRangePickerIsoProps = DateRangePickerCommonProps & {
  /** Range start, as an ISO string (e.g. from filter state), or null. */
  after: string | null;
  /** Range end, as an ISO string (e.g. from filter state), or null. */
  before: string | null;
  /** Called with day-bounded ISO strings (00:00:00 / 23:59:59), or (null, null) on clear. */
  onChange: (after: string | null, before: string | null) => void;
  value?: never;
};

export type DateRangePickerProps = DateRangePickerValueProps | DateRangePickerIsoProps;

const isIsoProps = (props: DateRangePickerProps): props is DateRangePickerIsoProps =>
  "after" in props || "before" in props;

/**
 * Accepts either a `Date` tuple (`value`/`onChange`) or filter state stored as
 * two separate ISO date strings (`after`/`before`/`onChange`). Either way,
 * the selected range is clamped to day bounds (00:00:00 / 23:59:59) before
 * being reported.
 */
export function DateRangePicker(props: DateRangePickerProps) {
  const { t } = useTranslation("shared");
  const {
    placeholder = t("dateRangePicker.placeholder"),
    className,
    disabled,
    dateFormat = "dd/MM/yyyy",
  } = props;

  const value: DateRange | null = isIsoProps(props)
    ? props.after && props.before
      ? [new Date(props.after), new Date(props.before)]
      : null
    : props.value ?? null;

  const handleChange = (range: DateRange | null) => {
    if (!range) {
      if (isIsoProps(props)) props.onChange(null, null);
      else props.onChange?.(null);
      return;
    }
    const clamped: DateRange = [
      new Date(new Date(range[0]).setHours(0, 0, 0, 0)),
      new Date(new Date(range[1]).setHours(23, 59, 59, 999)),
    ];
    if (isIsoProps(props)) {
      props.onChange(clamped[0].toISOString(), clamped[1].toISOString());
    } else {
      props.onChange?.(clamped);
    }
  };

  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const [localRange, setLocalRange] = React.useState<DayPickerDateRange | undefined>(undefined);
  const [hasStarted, setHasStarted] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setLocalRange(undefined);
      setHasStarted(false);
    }
  }, [open]);

  const handleSelect = (range: DayPickerDateRange | undefined) => {
    if (!hasStarted) {
      // first click
      if (range?.from) {
        setLocalRange({ from: range.from });
        setHasStarted(true);
      }
    } else {
      // second click
      if (!range?.from) {
        setLocalRange(undefined);
        setHasStarted(false);
        return;
      }
      handleChange([range.from, range.to ?? range.from]);
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleChange(null);
  };

  const hasValue = !!value;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-[2.4rem] w-full max-w-[330px] cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-left text-sm transition-colors hover:border-gray-400",
            open && "border-blue-600",
            disabled && "pointer-events-none bg-gray-50 opacity-50",
            className,
          )}
        >
          <CalendarIcon className="size-4 shrink-0 text-gray-400" />
          <span className={cn("flex-1 truncate", hasValue ? "text-gray-900" : "text-gray-400")}>
            {hasValue
              ? `${format(value[0], dateFormat, { locale: fr })} à ${format(value[1], dateFormat, { locale: fr })}`
              : placeholder}
          </span>
          {hasValue && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 text-gray-400 hover:text-gray-600"
            >
              <X className="size-3.5" />
            </button>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={localRange}
          showOutsideDays={false}
          fixedWeeks
          onSelect={handleSelect}
          numberOfMonths={isMobile ? 1 : 2}
          defaultMonth={isMobile ? undefined : subMonths(new Date(), 1)}
        />
      </PopoverContent>
    </Popover>
  );
}
